/**
 * WordPress dependencies
 */
import { useDispatch, useSelect } from '@wordpress/data';
import { _x } from '@wordpress/i18n';

/**
 * External dependencies
 */
import { trim } from 'lodash';
import { ReusableEntitiesDialog } from '@nab/components';
import { store as NAB_DATA } from '@nab/data';
import { createGoal, isDefined } from '@nab/utils';
import type { Goal, ReusableGoal } from '@nab/types';

/**
 * Internal dependencies
 */
import { store as NAB_EDITOR } from '../../store';
import { createConversionAction } from '@nab/conversion-actions';

export const ReusableGoalDialog = (): JSX.Element | null => {
	const dialog = useReusableDialog();
	const { closeReusableDialog: onClose } = useDispatch( NAB_EDITOR );

	const {
		activeGoal,
		areThereConversionActions,
		currentGoals,
		isSaving,
		reusableGoals,
	} = useSelect(
		( select ) => ( {
			activeGoal: select( NAB_EDITOR ).getActiveGoal(),
			areThereConversionActions: select( NAB_EDITOR )
				.getGoals()
				.some(
					( g ) =>
						!! select( NAB_EDITOR ).getConversionActions( g.id )
							.length
				),
			currentGoals: select( NAB_EDITOR ).getGoals(),
			isSaving: select( NAB_DATA ).isSavingReusableEntities(),
			reusableGoals: select( NAB_DATA ).getReusableGoals(),
		} ),
		[]
	);
	const conversionActions = useSelect(
		( select ) =>
			activeGoal?.id
				? select( NAB_EDITOR ).getConversionActions( activeGoal.id )
				: [],
		[ activeGoal ]
	);

	const { saveReusableGoals } = useDispatch( NAB_DATA );
	const {
		addConversionActionsIntoGoal,
		addGoals,
		removeGoals,
		setActiveGoal,
	} = useDispatch( NAB_EDITOR );

	const mustUseReplace =
		currentGoals.every( ( g ) => ! trim( g.attributes.name ) ) &&
		! areThereConversionActions;

	if ( 'manage-goals' === dialog ) {
		return (
			<ReusableEntitiesDialog< ReusableGoal >
				mode="manage"
				labels={ {
					title: _x( 'Reusable Goals', 'text', 'nelio-ab-testing' ),
					manageElements: _x(
						'Edit or delete the following reusable goals:',
						'user',
						'nelio-ab-testing'
					),
					replace: mustUseReplace
						? _x( 'Import', 'command', 'nelio-ab-testing' )
						: undefined,
					selectMultiple: _x(
						'Select one or more reusable goals:',
						'user',
						'nelio-ab-testing'
					),
				} }
				items={ reusableGoals }
				saving={ isSaving }
				onAdd={
					mustUseReplace
						? undefined
						: ( selection ) => {
								const newGoals = selection.map( goalify );
								void addGoals( newGoals );
								newGoals.forEach(
									( g ) =>
										void addConversionActionsIntoGoal(
											g.id,
											g.conversionActions
										)
								);
								if ( newGoals[ 0 ] ) {
									void setActiveGoal( newGoals[ 0 ].id );
								}
						  }
				}
				onReplace={ ( selection ) => {
					void removeGoals( currentGoals.map( ( g ) => g.id ) );
					const newGoals = selection.map( goalify );
					void addGoals( newGoals );
					if ( newGoals[ 0 ] ) {
						void setActiveGoal( newGoals[ 0 ].id );
					}
					newGoals.forEach(
						( g ) =>
							void addConversionActionsIntoGoal(
								g.id,
								g.conversionActions
							)
					);
				} }
				onSave={ saveReusableGoals }
				onClose={ onClose }
			/>
		);
	}

	if ( activeGoal && 'save-goal' === dialog ) {
		return (
			<ReusableEntitiesDialog< ReusableGoal >
				mode="save"
				labels={ {
					title: _x( 'Reusable Goals', 'text', 'nelio-ab-testing' ),
					collectionName: _x(
						'Reusable Goals',
						'text',
						'nelio-ab-testing'
					),
					nameField: _x( 'Name', 'text', 'nelio-ab-testing' ),
					newItemOption: _x(
						'New Reusable Goal',
						'text',
						'nelio-ab-testing'
					),
				} }
				item={ { ...activeGoal, conversionActions } }
				items={ reusableGoals }
				saving={ isSaving }
				onSave={ saveReusableGoals }
				onClose={ onClose }
			/>
		);
	}

	return null;
};

// =====
// HOOKS
// =====

const useReusableDialog = () =>
	useSelect( ( select ) => select( NAB_EDITOR ).getReusableDialog(), [] );

const goalify = ( rg: ReusableGoal ): Goal => ( {
	...createGoal(),
	attributes: rg.attributes,
	conversionActions: rg.conversionActions
		.map( ( ca ) => {
			const newAction = createConversionAction( ca.type );
			if ( ! newAction ) {
				return undefined;
			}
			return { ...ca, id: newAction.id };
		} )
		.filter( isDefined ),
} );
